<template>
    <div
        ref="selectContainer"
        class="form-field select-container"
        @click.stop="toggleDropdown"
    >
        <div class="form-field__input">
            <label
                v-if="label"
                :for="id"
                class="form-field__label"
                :class="{ 'is-active': hasValue || isOpen }"
            >
                {{ label }}
            </label>

            <div
                :class="[
                    'select-field',
                    {
                        'select-field_open': isOpen,
                        'input-field_error': hasError,
                        'has-value': hasValue,
                        'input-field_disabled': disabled,
                    },
                ]"
            >
                <span
                    :class="{ 'has-value': hasValue }"
                    :style="valueStyle"
                >
                    {{ selectedOptionLabel }}
                </span>

                <div
                    v-if="!disabled && hasValue"
                    class="select-field__clear"
                    @click.stop="clearSelection"
                >
                    <img v-svg-inline src="@/assets/icons/ui/field-clear.svg" />
                </div>

                <img
                    v-svg-inline
                    src="@/assets/icons/ui/select-arrow.svg"
                    class="select-field__arrow"
                    :class="{ 'select-field__arrow_open': isOpen }"
                />
            </div>

            <div v-if="isOpen" class="select-field__dropdown">
                <div v-if="searchable" class="select-field__search">
                    <input
                        v-model="searchTerm"
                        type="text"
                        placeholder="Введите текст"
                        @click.stop
                    />
                </div>

                <div
                    v-for="option in filteredOptions"
                    :key="option.value"
                    class="select-field__option"
                    :class="{ 'select-field__option_selected': isOptionSelected(option) }"
                    @click.stop="selectOption(option)"
                >
                    <template v-if="multiple">
                        <UICheckbox
                            :model-value="isOptionSelected(option)"
                            @update:model-value="selectOption(option)"
                        />

                        <span>{{ option.label }}</span>
                    </template>
                    <template v-else>
                        {{ option.label }}
                    </template>
                </div>
            </div>
        </div>

        <span v-if="hasError" class="form-field__error">
            {{ errorMessage }}
        </span>
    </div>
</template>

<script lang="ts" setup>
    import {
        ref,
        computed,
        onMounted,
        onBeforeUnmount,
        defineProps,
        defineEmits,
    } from 'vue';
    import type { PropType } from 'vue';

    import UICheckbox from '@/components/ui/UICheckbox/UICheckbox.vue';

    interface Option {
        value: string | number,
        label: string | number,
    }

    const props = defineProps({
        modelValue: {
            type: [String, Number, Array] as PropType<string | number | (string | number)[] | null | undefined>,
            default: undefined,
        },
        options: {
            type: Array as PropType<Option[]>,
            default: () => [],
        },
        placeholder: {
            type: String,
            default: '',
        },
        label: {
            type: String,
            default: '',
        },
        errorMessage: {
            type: String,
            default: '',
        },
        id: {
            type: String,
            default: '',
        },
        searchable: {
            type: Boolean,
            default: false,
        },
        multiple: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    });

    const emit = defineEmits<{
        (e: 'update:modelValue', value: string | number | (string | number)[] | undefined): void,
    }>();

    const isOpen = ref(false);
    const selectContainer = ref<HTMLElement | null>(null);
    const searchTerm = ref('');

    const filteredOptions = computed<Option[]>(() => {
        const list = props.options || [];
        if (!props.searchable || !searchTerm.value) {
            return list;
        }

        return list.filter((option) =>
            String(option.label).toLowerCase().includes(searchTerm.value.toLowerCase()));
    });

    const selectedOptionLabel = computed<string>(() => {
        if (props.multiple) {
            const arrayOfValues = getSelectedArray();
            const count = arrayOfValues.length;

            if (count === 0) {
                return props.placeholder;
            }

            if (count === 1) {
                const singleOption = (props.options || []).find(opt =>
                    opt.value === arrayOfValues[0]);

                return singleOption ? String(singleOption.label) : props.placeholder;
            }

            return `Выбрано: ${count}`;
        }

        const selectedOption = (props.options || []).find(
            (option) => option.value === props.modelValue,
        );

        return selectedOption ? String(selectedOption.label) : props.placeholder;
    });

    const valueStyle = computed<string>(() => {
        // алгоритм расчета следующий
        // отступы слева/справа по 16px
        // треугольник 12px
        // крестик шириной 16px
        // отступ крестика от правого края 35px, но уже подсчитан отступ и ширина треугольника, поэтому 35 - 12 - 16 = 7px
        const subWidth = 16 + 16 + 12 + 16 + 7;

        return `width: calc(100% - ${subWidth}px);`;
    });

    const hasValue = computed<boolean>(() => {
        if (props.multiple) {
            return Array.isArray(props.modelValue) && props.modelValue.length > 0;
        }

        return (
            props.modelValue !== undefined &&
            props.modelValue !== null &&
            props.modelValue !== ''
        );
    });

    const hasError = computed<boolean>(() => !!props.errorMessage);

    function selectOption(option: Option): void {
        if (props.multiple) {
            const arrayOfValues = getSelectedArray();
            const valueIndex = arrayOfValues.indexOf(option.value);

            if (valueIndex >= 0) {
                arrayOfValues.splice(valueIndex, 1);
                emit('update:modelValue', [...arrayOfValues]);
            }
            else {
                emit('update:modelValue', [...arrayOfValues, option.value]);
            }
        }
        else {
            emit('update:modelValue', option.value);
            isOpen.value = false;
        }
    }

    function clearSelection(): void {
        if (props.multiple) {
            emit('update:modelValue', []);
        }
        else {
            emit('update:modelValue', undefined);
        }
    }

    function toggleDropdown(): void {
        if (props.disabled) {
            return;
        }

        if (!isOpen.value) {
            document.dispatchEvent(new CustomEvent('close-all-select-dropdowns'));
        }

        isOpen.value = !isOpen.value;
    }

    function handleClickOutside(event: MouseEvent): void {
        if (
            selectContainer.value &&
            !selectContainer.value.contains(event.target as Node)
        ) {
            isOpen.value = false;
        }
    }

    function closeAllSelectDropdowns(): void {
        searchTerm.value = '';
        isOpen.value = false;
    }

    function isOptionSelected(option: Option): boolean {
        if (props.multiple) {
            return getSelectedArray().includes(option.value);
        }

        return props.modelValue === option.value;
    }

    function getSelectedArray(): (string | number)[] {
        if (Array.isArray(props.modelValue)) {
            return props.modelValue;
        }

        if (
            props.modelValue !== undefined &&
            props.modelValue !== null &&
            props.modelValue !== ''
        ) {
            return [props.modelValue];
        }

        return [];
    }

    onMounted(() => {
        window.addEventListener('click', handleClickOutside);
        document.addEventListener('close-all-select-dropdowns', closeAllSelectDropdowns);
    });

    onBeforeUnmount(() => {
        window.removeEventListener('click', handleClickOutside);
        document.removeEventListener('close-all-select-dropdowns', closeAllSelectDropdowns);
    });
</script>

<style lang="scss" scoped>
    @use "./UISelect.scss" as *;
</style>
